import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import {
  TrendingUp, AlertTriangle, BookOpen, Download, Calendar,
  CheckCircle2, Users, Star, Info, MessageSquare, Trophy
} from 'lucide-react';

const fbColor = (s) =>
  s == null ? 'text-gray-400'
    : s >= 4.5 ? 'text-emerald-600'
    : s >= 3.5 ? 'text-levelup-teal'
    : s >= 2.5 ? 'text-amber-500'
    : 'text-red-500';

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
};

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl shadow-md p-5 border-l-4" style={{ borderLeftColor: color }} data-testid={`cp-summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-xs">{label}</p>
        <p className="text-2xl font-bold text-levelup-dark mt-1">{value}</p>
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon style={{ color }} size={20} />
      </div>
    </div>
  </div>
);

export const ContentPerformanceTab = ({ appliedFrom, appliedTo, solutionAreas = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [area, setArea] = useState('all');

  useEffect(() => {
    setLoading(true);
    setError(false);
    const p = new URLSearchParams({ solution_area: area });
    if (appliedFrom) p.append('from_date', appliedFrom);
    if (appliedTo) p.append('to_date', appliedTo);
    api.request(`/analytics/content-performance?${p.toString()}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [appliedFrom, appliedTo, area]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin w-10 h-10 border-4 border-levelup-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700" data-testid="cp-error">
        <AlertTriangle size={18} /> Couldn't load content performance data. Please try again.
      </div>
    );
  }

  const courses = data?.courses || [];
  const summary = data?.summary || {};
  const engaging = courses.filter(c => c.engagement_score > 0).slice(0, 15);
  const funnel = courses.filter(c => c.downloads > 0 || c.scheduled > 0).slice(0, 8);
  const gaps = courses.filter(c => c.is_gap);
  const lowFb = summary.low_feedback_threshold ?? 3.5;
  const notConv = summary.not_converting_downloads ?? 10;

  return (
    <div className="space-y-6" data-testid="content-performance-tab">
      {/* Solution Area filter */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Solution Area</label>
          <select
            value={area}
            onChange={e => setArea(e.target.value)}
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent"
            data-testid="cp-solution-area-filter"
          >
            <option value="all">All Solution Areas</option>
            {solutionAreas.map(sa => <option key={sa} value={sa}>{sa}</option>)}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard icon={BookOpen} label="Courses" value={summary.total_courses || 0} color="#0D3B36" />
        <SummaryCard icon={Download} label="Downloads" value={summary.total_downloads || 0} color="#9333EA" />
        <SummaryCard icon={CheckCircle2} label="Completed" value={summary.total_completed_workshops || 0} color="#10B981" />
        <SummaryCard icon={Star} label="Avg Feedback" value={summary.avg_feedback_overall != null ? `${summary.avg_feedback_overall}/5` : '—'} color="#F59E0B" />
        <SummaryCard icon={AlertTriangle} label="Content Gaps" value={summary.content_gaps || 0} color="#EF4444" />
      </div>

      {/* Section 1: Most Engaging Courses */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-3 mb-1">
          <Trophy size={22} className="text-levelup-accent" />
          <h2 className="text-lg font-bold text-levelup-dark">Most Engaging Courses</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Ranked by engagement score = downloads + (scheduled × 2) + (completed × 3) + learners trained.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-levelup-dark text-white">
                <th className="px-3 py-2.5 text-left text-xs font-semibold">#</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold">Course</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold">Solution Area</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Downloads</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Scheduled</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Completed</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Learners</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Avg Feedback</th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {engaging.map((c, i) => (
                <tr key={c.course_id} className="hover:bg-gray-50" data-testid={`cp-engaging-row-${i}`}>
                  <td className="px-3 py-2.5 text-sm text-gray-500 font-bold">{i + 1}</td>
                  <td className="px-3 py-2.5 text-sm font-medium text-levelup-dark max-w-[260px] truncate" title={c.title}>{c.title}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-500">{c.solution_area || '—'}</td>
                  <td className="px-3 py-2.5 text-sm text-right text-gray-700">{c.downloads}</td>
                  <td className="px-3 py-2.5 text-sm text-right text-gray-700">{c.scheduled}</td>
                  <td className="px-3 py-2.5 text-sm text-right text-gray-700">{c.completed}</td>
                  <td className="px-3 py-2.5 text-sm text-right font-semibold text-levelup-teal">{c.learners}</td>
                  <td className={`px-3 py-2.5 text-sm text-right font-semibold ${fbColor(c.avg_feedback)}`}>
                    {c.avg_feedback != null ? c.avg_feedback : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-right font-bold text-levelup-dark">{c.engagement_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {engaging.length === 0 && <p className="text-gray-500 text-center py-8">No engagement data yet for this selection.</p>}
        </div>
        {engaging.length >= 15 && <p className="text-xs text-gray-400 mt-3">Showing top 15 courses by engagement score.</p>}
      </div>

      {/* Section 2: Content Funnel */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-3 mb-1">
          <TrendingUp size={22} className="text-levelup-accent" />
          <h2 className="text-lg font-bold text-levelup-dark">Content Funnel — Downloaded → Scheduled → Completed</h2>
        </div>
        <p className="text-sm text-gray-500 mb-5">Spot where interest drops off. Conversion rates are shown beside each stage.</p>
        <div className="flex items-center gap-5 mb-4 text-xs text-gray-500 font-medium">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#9333EA' }} /> Downloaded</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#F97316' }} /> Scheduled</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: '#10B981' }} /> Completed</span>
        </div>
        <div className="space-y-5">
          {funnel.map((c) => {
            const base = Math.max(c.downloads, c.scheduled, c.completed, 1);
            const pct = (v) => `${Math.max((v / base) * 100, v > 0 ? 6 : 0)}%`;
            const dlToSched = c.downloads > 0 ? Math.round((c.scheduled / c.downloads) * 100) : null;
            const schedToComp = c.scheduled > 0 ? Math.round((c.completed / c.scheduled) * 100) : null;
            return (
              <div key={c.course_id} data-testid={`cp-funnel-row-${c.course_id}`}>
                <p className="text-sm font-medium text-levelup-dark mb-1.5 truncate" title={c.title}>{c.title}</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="h-5 rounded text-white text-xs flex items-center px-2 font-medium min-w-[2rem]" style={{ width: pct(c.downloads), backgroundColor: '#9333EA' }}>{c.downloads}</div>
                    <span className="text-[11px] text-gray-400">downloads</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-5 rounded text-white text-xs flex items-center px-2 font-medium min-w-[2rem]" style={{ width: pct(c.scheduled), backgroundColor: '#F97316' }}>{c.scheduled}</div>
                    <span className="text-[11px] text-gray-400">scheduled{dlToSched != null && <span className="ml-1 text-gray-500 font-medium">({dlToSched}% of downloads)</span>}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-5 rounded text-white text-xs flex items-center px-2 font-medium min-w-[2rem]" style={{ width: pct(c.completed), backgroundColor: '#10B981' }}>{c.completed}</div>
                    <span className="text-[11px] text-gray-400">completed{schedToComp != null && <span className="ml-1 text-gray-500 font-medium">({schedToComp}% of scheduled)</span>}</span>
                  </div>
                </div>
              </div>
            );
          })}
          {funnel.length === 0 && <p className="text-gray-500 text-center py-8">No funnel data yet for this selection.</p>}
        </div>
        {funnel.length >= 8 && <p className="text-xs text-gray-400 mt-3">Showing top 8 courses by activity.</p>}
      </div>

      {/* Section 3: Content Gaps */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-3 mb-1">
          <AlertTriangle size={22} className="text-red-500" />
          <h2 className="text-lg font-bold text-levelup-dark">Content Gaps</h2>
        </div>
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-sm">
          <Info size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <span className="text-amber-800">
            Auto-flagged: <strong>avg feedback &lt; {lowFb}/5</strong> ("Needs review") or
            <strong> ≥ {notConv} downloads but 0 workshops</strong> ("Interest not converting").
            Use these signals to prioritise content investment.
          </span>
        </div>
        {gaps.length === 0 ? (
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm">
            <CheckCircle2 size={18} /> No content gaps detected for this selection.
          </div>
        ) : (
          <div className="space-y-4">
            {gaps.map((c) => (
              <div key={c.course_id} className="border border-gray-200 rounded-xl p-4" data-testid={`cp-gap-row-${c.course_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-levelup-dark">{c.title}</p>
                    <p className="text-xs text-gray-500">{c.solution_area || '—'} · {c.downloads} downloads · {c.scheduled} scheduled · {c.completed} completed</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {c.flags?.low_feedback && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1">
                        <Star size={12} /> Needs review · {c.avg_feedback}/5
                      </span>
                    )}
                    {c.flags?.not_converting && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 flex items-center gap-1">
                        <TrendingUp size={12} /> Interest not converting
                      </span>
                    )}
                  </div>
                </div>
                {c.comments?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 flex items-center gap-1.5 mb-2">
                      <MessageSquare size={14} /> Trainer feedback ({c.comments.length})
                    </p>
                    <div className="space-y-2 max-h-44 overflow-y-auto">
                      {c.comments.map((cm, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-2.5 text-sm">
                          <p className="text-gray-700">"{cm.text}"</p>
                          <p className="text-[11px] text-gray-400 mt-1">
                            {cm.score != null && <span className={`font-semibold ${fbColor(cm.score)}`}>{cm.score}/5</span>}
                            {cm.date && <span className="ml-2">{fmtDate(cm.date)}</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
